/**
 * DotNode component for Infrastructure Map
 *
 * Renders an endpoint as a minimal dot representation.
 * Used for linked but untagged endpoints to show connectivity without taking canvas space.
 */

import { useRef, useState, type ReactElement } from 'react'
import type { CanvasPosition } from '../../../types/infrastructure-map'
import { Dot, DotTooltip } from './dot-node/dot-node.parts'
import { useDotDrag } from './dot-node/use-dot-drag'

interface DotNodeProps {
  endpointId: string
  hostname: string
  ipAddress: string
  position: CanvasPosition
  scale: number
  isSelected: boolean
  isInCollectionSet: boolean
  onDrag?: (position: CanvasPosition) => void
  onDragEnd?: (position: CanvasPosition) => void
  onClick?: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
}

export function DotNode({
  endpointId,
  hostname,
  ipAddress,
  position,
  scale,
  isSelected,
  isInCollectionSet,
  onDrag,
  onDragEnd,
  onClick,
  onContextMenu,
}: DotNodeProps): ReactElement {
  const nodeRef = useRef<HTMLDivElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const dragArgs: Parameters<typeof useDotDrag>[0] = { nodeRef, scale, position }
  if (onDrag) dragArgs.onDrag = onDrag
  if (onDragEnd) dragArgs.onDragEnd = onDragEnd
  const { isDragging, handleMouseDown } = useDotDrag(dragArgs)

  const cursorClass = onDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
  const zClass = isDragging ? 'z-50' : 'z-10'

  return (
    <div
      ref={nodeRef}
      data-node="true"
      data-endpoint-id={endpointId}
      className={`absolute ${cursorClass} transition-all ${zClass}`}
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'auto',
      }}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => {
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(e)
      }}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(e)
      }}
    >
      <Dot isInCollectionSet={isInCollectionSet} isSelected={isSelected} isHovered={isHovered} />
      {isHovered && !isDragging && <DotTooltip hostname={hostname} ipAddress={ipAddress} />}
    </div>
  )
}

export default DotNode
